/**
 * Spor A step 3 T1.3: the "Dan epikrise" form (DIS01/02/03/05-S). Communicate
 * builds the XML (T1.2, `api/src/dis/epikrise.ts`); these helpers mirror its
 * limits and rules so the form can warn and block before anything is built.
 *
 * - Markering (DIS01-S 4.10–4.16, DIS02/03-S 4.11–4.18) follows MedCom's
 *   "Inspiration til implementering af markering": four fixed answers, default
 *   "Ingen markering" (ActionPriority omitted). Any other answer makes the field
 *   "Anbefaling om opfølgning hos egen læge" mandatory (tx..1750).
 * - Text01 template: the headings of VEJ nr 10036 af 30/11/2018 afsnit 2.3–2.8,
 *   each with its guide text quoted from the vejledning.
 * - Markup: line breaks and [b]…[/b], [i]…[/i], [u]…[/u] (Bold/Italic/Underline,
 *   FormattedText.xsd). Markup cannot nest. Tags are not counted as characters.
 */

import type { PickedReceiver } from '@/components/patient/ReferralSorPicker';

export const EPIKRISE_KINDS = ['XDIS01', 'XDIS02', 'XDIS03', 'XDIS05'] as const;
export type EpikriseKind = (typeof EPIKRISE_KINDS)[number];

export function isEpikriseKind(value: unknown): value is EpikriseKind {
  return typeof value === 'string' && (EPIKRISE_KINDS as readonly string[]).includes(value);
}

/** Recommendation tx..1750 (XDIS01/02/03 facitliste; DIS01-S 4.15, DIS02/03-S 4.17). */
export const EPIKRISE_RECOMMENDATION_MAX = 1750;
/** ClinicalInformation/Text01 tx..3150 per block. */
export const EPIKRISE_TEXT01_BLOCK_MAX = 3150;
/** Aktionstillægsdiagnoser (MainAdditional) ≤ 5 (DIS05-S 4.10; XSD maxOccurs 5). */
export const EPIKRISE_MAIN_ADDITIONAL_MAX = 5;
/** Relative maxOccurs 2. */
export const EPIKRISE_RELATIVES_MAX = 2;
export const EPIKRISE_CONTACT_MAX = 35;

/**
 * Total Text01 characters: min(testprotokol, maxOccurs × 3150). XDIS01/02/03 have
 * 9 ClinicalInformation (28.350; DIS01-S 4.22 states 31.150, which the XSD cannot
 * carry). XDIS05 has 10 (31.500), and DIS05-S 4.19 allows 28.000.
 */
export const EPIKRISE_TEXT01_LIMIT: Readonly<Record<EpikriseKind, number>> = {
  XDIS01: 28350,
  XDIS02: 28350,
  XDIS03: 28350,
  XDIS05: 28000,
};

/** Warn the user from this share of the limit (DIS05-S 4.19 "Bruger skal advares om begrænsning"). */
export const EPIKRISE_TEXT_WARN_SHARE = 0.9;

/** XDIS05 (2005/08/07 RadiologyReport) has no ActionPriority or RecommendedAction. */
export function epikriseHasMarkering(kind: EpikriseKind): boolean {
  return kind !== 'XDIS05';
}

/** XDIS01/02/03: exactly one aktionsdiagnose. XDIS05: at most one (DIS05-S 4.9, not required). */
export function epikriseRequiresMainDiagnosis(kind: EpikriseKind): boolean {
  return kind !== 'XDIS05';
}

/** XDIS05 only (DIS05-S 4.17, 4.22, bilag); XDIS01/02/03 are unchanged. */
export function epikriseHasRadiologyExtras(kind: EpikriseKind): boolean {
  return kind === 'XDIS05';
}

/** Diagnose/Other maxOccurs 50 (ClinicalTypes.xsd DiagnosesType). */
export const EPIKRISE_OTHER_MAX = 50;
export const EPIKRISE_DIAGNOSE_CODE_MAX = 17;
export const EPIKRISE_DIAGNOSE_TEXT_MAX = 70;

/** DiagnoseDescriptionCodeType (2005/08/07 ValueLists.xsd), with the facitliste's Danish names. */
export const EPIKRISE_OTHER_DESCRIPTION_CODES = [
  { value: 'roentgenundersoegelse', label: 'Røntgenundersøgelse' },
  { value: 'operation', label: 'Operation/undersøgelse' },
  { value: 'bidiagnose', label: 'Bidiagnose' },
  { value: 'tillaegsdiagnose', label: 'Tillægsdiagnose' },
  { value: 'aktionsdiagnose', label: 'Aktionsdiagnose' },
  { value: 'midlertidig_diagnose', label: 'Midlertidig diagnose' },
  { value: 'forloebsdiagnose', label: 'Forløbsdiagnose' },
  { value: 'henv_diagnose', label: 'Henvisningsdiagnose' },
  { value: 'uspec_diagnose', label: 'Uspecificeret diagnose' },
] as const;
export type EpikriseOtherDescriptionCode = (typeof EPIKRISE_OTHER_DESCRIPTION_CODES)[number]['value'];

/** DiagnoseTypeCodeType (2005/08/07 ValueLists.xsd). */
export const EPIKRISE_DIAGNOSE_TYPE_CODES = [
  { value: 'SKSdiagnosekode', label: 'SKS-kode' },
  { value: 'ICPCkode', label: 'ICPC-kode' },
  { value: 'uspecificeretkode', label: 'Uspecificeret kode' },
] as const;
export type EpikriseDiagnoseTypeCode = (typeof EPIKRISE_DIAGNOSE_TYPE_CODES)[number]['value'];

/** One "yderligere" diagnose/behandlingskode (Diagnose/Other) with DiagnoseDateTime. */
export type EpikriseOtherDiagnosisInput = {
  descriptionCode: EpikriseOtherDescriptionCode;
  typeCode: EpikriseDiagnoseTypeCode;
  code: string;
  text: string;
  date: string;
  time: string;
};

export function emptyOtherDiagnosis(): EpikriseOtherDiagnosisInput {
  return { descriptionCode: 'roentgenundersoegelse', typeCode: 'SKSdiagnosekode', code: '', text: '', date: '', time: '' };
}

export type Markering = 'none' | 'Akut' | 'Subakut' | 'Rutine';

/** The exact dropdown answers ("Inspiration til implementering af markering"). */
export const MARKERING_QUESTION = 'Indeholder epikrisen anbefalinger om opfølgning hos praktiserende læge?';
export const MARKERING_OPTIONS: ReadonlyArray<{ value: Markering; colour: 'red' | 'yellow' | 'green' | 'white'; text: string }> = [
  { value: 'none', colour: 'white', text: 'Nej, indeholder IKKE anbefalinger om opfølgning hos praktiserende læge.' },
  { value: 'Akut', colour: 'red', text: 'Ja, opfølgningen bør ske akut inden for 1-2 hverdage!' },
  {
    value: 'Subakut',
    colour: 'yellow',
    text: 'Ja, patienten er sårbar og har behov for opsøgende indsats fra praktiserende læge.',
  },
  {
    value: 'Rutine',
    colour: 'green',
    text: 'Ja, patienten eller dennes netværk/pårørende kan selv tage kontakt til praktiserende læge.',
  },
];
export const RECOMMENDATION_HEADING = 'Anbefaling om opfølgning hos egen læge';

export type EpikriseSectionId = 'resume' | 'patientinformation' | 'medicin' | 'forebyggelse' | 'ikke_afsluttede' | 'medicinliste';

/** VEJ nr 10036 af 30/11/2018, afsnit 2.3–2.8 (2.2 is the recommendation field). */
export const EPIKRISE_SECTIONS: ReadonlyArray<{ id: EpikriseSectionId; vej: string; heading: string; guide: string }> = [
  {
    id: 'resume',
    vej: '2.3',
    heading: 'Resumé af behandlingsforløb',
    guide:
      'Resuméet af behandlingsforløbet skal indeholde: a) årsag til indlæggelse/ambulant forløb samt henvisningsdiagnoser; b) relevante diagnoser, væsentlige behandlinger og undersøgelser samt væsentlige overvejelser og fund; c) eventuelt svar på vigtige parakliniske undersøgelsesresultater, egen læge skal være særligt opmærksom på, fx billeddiagnostik, laboratoriesvar og patologisvar; d) eventuel tvangsbehandling … hvis det vurderes relevant for egen læge.',
  },
  {
    id: 'patientinformation',
    vej: '2.4',
    heading: 'Patientinformation og -aftale',
    guide:
      'Der skal angives en tydelig beskrivelse af relevant information, der er givet til patienten, samt hvad der er aftalt med patienten og eventuelt med pårørende. Hvis behandlingen er foretaget på baggrund af et stedfortrædende samtykke, skal det også fremgå, hvem der har samtykket, samt hvad der er informeret om og tilkendegivet i den forbindelse.',
  },
  {
    id: 'medicin',
    vej: '2.5',
    heading: 'Medicinoplysninger',
    guide:
      'a) Ved nye medicinordinationer skal indikation fremgå af epikrisen. b) Hvis der er ændret i patientens eksisterende medicinske behandling, skal ændringen begrundes. c) Det skal fremgå, hvornår nye ordinationer og/eller ændringer eventuelt skal ophøre, påbegyndes eller ændres. d) Det skal fremgå, hvis der er behov for hjælp til medicindispensering og/eller -administration. e) Relevante oplysninger om ændring i dosisdispensering. f) Hvis patienten er givet medicin med, skal mængde og type oplyses.',
  },
  {
    id: 'forebyggelse',
    vej: '2.6',
    heading: 'Forebyggelses- og sundhedsfremmende plan',
    guide:
      'Epikrisen skal, hvor det er relevant, indeholde: a) funktionsniveau ved udskrivelsen eller afslutningen; b) forslag til sygemelding og forventet varighed; c) socialmedicinske foranstaltninger, der er sat i værk, herunder kontakt til hjemmepleje eller socialforvaltning; d) potentielle senfølger eller symptomer på recidiv; e) sundhedsmæssige risikofaktorer, fx rygning, alkohol, fejlernæring, fysisk inaktivitet m.v.',
  },
  {
    id: 'ikke_afsluttede',
    vej: '2.7',
    heading: 'Ikke-afsluttede og planlagte undersøgelser samt henvisninger',
    guide:
      'Det skal fremgå, hvis væsentlige undersøgelsesresultater ikke foreligger ved afsendelse af epikrisen, og at disse eftersendes, og hvem der har ansvaret for at sikre, at patienten bliver informeret om resultatet. Det skal fremgå, hvis der er planlagt eller iværksat undersøgelser m.v. på behandlingsstedet, og hvis patienten er blevet henvist til andre behandlingssteder efter udskrivelse eller afslutning.',
  },
  {
    id: 'medicinliste',
    vej: '2.8',
    heading: 'Medicinliste',
    guide:
      'Patientens ajourførte medicinliste skal indsættes i overskuelig form til sidst i epikrisen. Medicinlisten skal være identisk med medicinoplysninger i FMK og stemme overens med den medicinliste, som er udleveret til patienten.',
  },
];

export type EpikriseDiagnosis = { code: string; text: string };
export type EpikriseRelativeInput = { relationCode: 'uspec_paaroerende' | 'mor' | 'far' | 'barn'; surname: string; givenName: string };
export type EpikriseForm = {
  kind: EpikriseKind;
  episodeOfCareStatusCode: 'inaktiv' | 'indlagt' | 'ambulant' | 'doed' | 'ambulant_roentgen';
  start: { date: string; time: string };
  end: { date: string; time: string };
  contact: { title: string; name: string };
  markering: Markering;
  recommendation: string;
  mainDiagnosis: EpikriseDiagnosis | null;
  mainAdditional: EpikriseDiagnosis[];
  /** XDIS05: yderligere undersøgelsesdiagnoser/behandlingskoder (DIS05-S 4.17). */
  otherDiagnoses: EpikriseOtherDiagnosisInput[];
  sections: Record<EpikriseSectionId, string>;
  includeReferralRelative: boolean;
  relatives: EpikriseRelativeInput[];
  /** A receiver chosen in SOR (DIS05-S 4.4); null = the referral's referrer/sender. */
  receiver: PickedReceiver | null;
  /** CCReceiver ("med kopi til egen læge"), chosen in SOR. */
  ccReceiver: PickedReceiver | null;
  /** Build the identical copy to the CC receiver's location number right after the original (DIS05-S 4.3). */
  copyToCc: boolean;
};

export function defaultEpisodeStatus(kind: EpikriseKind): EpikriseForm['episodeOfCareStatusCode'] {
  return kind === 'XDIS05' ? 'ambulant_roentgen' : kind === 'XDIS01' ? 'inaktiv' : 'ambulant';
}

export function emptyEpikriseForm(kind: EpikriseKind, contactTitle = 'Overlæge'): EpikriseForm {
  return {
    kind,
    episodeOfCareStatusCode: defaultEpisodeStatus(kind),
    start: { date: '', time: '' },
    end: { date: '', time: '' },
    contact: { title: contactTitle, name: '' },
    markering: 'none',
    recommendation: '',
    mainDiagnosis: null,
    mainAdditional: [],
    otherDiagnoses: [],
    sections: { resume: '', patientinformation: '', medicin: '', forebyggelse: '', ikke_afsluttede: '', medicinliste: '' },
    includeReferralRelative: true,
    relatives: [],
    receiver: null,
    ccReceiver: null,
    copyToCc: true,
  };
}

/** Restore a saved kladde; anything unreadable falls back to the empty form. */
export function readEpikriseForm(kind: EpikriseKind, raw: unknown): EpikriseForm {
  const base = emptyEpikriseForm(kind);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const r = raw as Partial<EpikriseForm>;
  return {
    ...base,
    ...r,
    kind,
    start: { ...base.start, ...(r.start ?? {}) },
    end: { ...base.end, ...(r.end ?? {}) },
    contact: { ...base.contact, ...(r.contact ?? {}) },
    sections: { ...base.sections, ...(r.sections ?? {}) },
    mainAdditional: Array.isArray(r.mainAdditional) ? r.mainAdditional.slice(0, EPIKRISE_MAIN_ADDITIONAL_MAX) : [],
    relatives: Array.isArray(r.relatives) ? r.relatives.slice(0, EPIKRISE_RELATIVES_MAX) : [],
    otherDiagnoses: readOtherDiagnoses(r.otherDiagnoses),
    markering: epikriseHasMarkering(kind) && r.markering ? r.markering : 'none',
    recommendation: epikriseHasMarkering(kind) ? (r.recommendation ?? '') : '',
  };
}

function readOtherDiagnoses(raw: unknown): EpikriseOtherDiagnosisInput[] {
  if (!Array.isArray(raw)) return [];
  const descriptionCodes = EPIKRISE_OTHER_DESCRIPTION_CODES.map((c) => c.value as string);
  const typeCodes = EPIKRISE_DIAGNOSE_TYPE_CODES.map((c) => c.value as string);
  return raw.slice(0, EPIKRISE_OTHER_MAX).map((item) => {
    const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    const str = (v: unknown) => (typeof v === 'string' ? v : '');
    const base = emptyOtherDiagnosis();
    return {
      descriptionCode: descriptionCodes.includes(str(o.descriptionCode))
        ? (str(o.descriptionCode) as EpikriseOtherDescriptionCode)
        : base.descriptionCode,
      typeCode: typeCodes.includes(str(o.typeCode)) ? (str(o.typeCode) as EpikriseDiagnoseTypeCode) : base.typeCode,
      code: str(o.code),
      text: str(o.text),
      date: str(o.date),
      time: str(o.time),
    };
  });
}

/** Diagnose/Other as Communicate receives it (XDIS05 only); time without date is refused in epikriseIssues. */
export function epikriseOtherDiagnosesForRequest(form: EpikriseForm): Array<{
  descriptionCode: EpikriseOtherDescriptionCode;
  typeCode: EpikriseDiagnoseTypeCode;
  code: string;
  text: string;
  dateTime?: { date: string; time?: string };
}> {
  if (!epikriseHasRadiologyExtras(form.kind)) return [];
  return form.otherDiagnoses.map((d) => ({
    descriptionCode: d.descriptionCode,
    typeCode: d.typeCode,
    code: d.code.trim(),
    text: d.text.trim(),
    ...(d.date ? { dateTime: d.time ? { date: d.date, time: d.time } : { date: d.date } } : {}),
  }));
}

/** The signature line for XDIS05 (DIS05-S 4.22): title and name from the login, date at build time. */
export function epikriseSignature(signer: { title: string | null; name: string | null }): string {
  return [signer.title?.trim(), signer.name?.trim()].filter(Boolean).join(' ');
}

/** Contact (DepartmentName = title, UnitName = name): XDIS05 signs with the login; other kinds use the typed values. */
export function epikriseContact(
  form: EpikriseForm,
  signer?: { name: string | null; title: string | null },
): { title: string; name: string } {
  if (epikriseHasRadiologyExtras(form.kind) && signer) {
    return { title: (signer.title?.trim() || form.contact.title.trim()), name: signer.name?.trim() ?? '' };
  }
  return { title: form.contact.title.trim(), name: form.contact.name.trim() };
}

export type MarkupProblem = 'nested' | 'unbalanced' | null;

/** Plain text of a markup string, or the markup problem the builder would refuse. */
export function parseMarkup(text: string): { plain: string; problem: MarkupProblem } {
  let open = '';
  let plain = '';
  let last = 0;
  for (const m of text.matchAll(/\[(\/?)([biu])\]/g)) {
    plain += text.slice(last, m.index);
    last = (m.index ?? 0) + m[0].length;
    if (m[1] !== '/') {
      if (open) return { plain, problem: 'nested' };
      open = m[2] ?? '';
    } else {
      if (open !== m[2]) return { plain, problem: 'unbalanced' };
      open = '';
    }
  }
  plain += text.slice(last);
  return { plain: plain.replace(/\r\n?/g, '\n'), problem: open ? 'unbalanced' : null };
}

/** Characters a markup text carries (text plus one per line break), as the builder counts. */
export function markupLength(text: string): number {
  return parseMarkup(text).plain.length;
}

function trimEnd(text: string): string {
  return text.replace(/\s+$/, '');
}

/** The sections the builder receives: heading + text, empty ones left out. */
export function epikriseSectionsForRequest(form: EpikriseForm): Array<{ heading: string; text: string }> {
  return EPIKRISE_SECTIONS.filter((s) => form.sections[s.id]?.trim()).map((s) => ({
    heading: s.heading,
    text: trimEnd(form.sections[s.id].replace(/\r\n?/g, '\n')),
  }));
}

/**
 * The count held against EPIKRISE_TEXT01_LIMIT, as Communicate's epikriseText01Count does.
 * XDIS05 (DIS05-S 4.19 "Der må max. indtastes 28.000 tegn"): the characters the user entered,
 * as they end up in the Text01 values after XML parsing: markup tags and escaping do not count,
 * CRLF/CR is one line break, trailing whitespace is trimmed; the section headings and blank
 * lines Clinio adds are not entered text. XDIS01/02/03 (unchanged): "Overskrift:" + line
 * break + text per section, two between sections.
 */
export function epikriseTextTotal(form: EpikriseForm): number {
  const sections = epikriseSectionsForRequest(form);
  if (form.kind === 'XDIS05') return sections.reduce((sum, s) => sum + markupLength(s.text), 0);
  return (
    sections.reduce((sum, s) => sum + s.heading.length + 1 + 1 + markupLength(s.text), 0) +
    2 * Math.max(0, sections.length - 1)
  );
}

export type EpikriseIssue =
  | 'main_diagnosis'
  | 'main_additional_max'
  | 'recommendation_missing'
  | 'recommendation_too_long'
  | 'recommendation_markup'
  | 'text_too_long'
  | 'text_markup'
  | 'contact_too_long'
  | 'relatives_max'
  | 'relative_surname'
  | 'end_before_start'
  | 'dates'
  | 'other_max'
  | 'other_incomplete'
  | 'other_too_long'
  | 'other_time_without_date'
  | 'signer_missing';

/**
 * `signer` (XDIS05, DIS05-S 4.22): the logged-in user's name and clinic title. When given,
 * the name is required and replaces the typed kontaktperson; a missing title falls back to
 * the typed title.
 */
export function epikriseIssues(
  form: EpikriseForm,
  referralRelative: boolean,
  signer?: { name: string | null; title: string | null },
): EpikriseIssue[] {
  const issues: EpikriseIssue[] = [];
  if (epikriseRequiresMainDiagnosis(form.kind) && !form.mainDiagnosis) issues.push('main_diagnosis');
  if (form.mainAdditional.length > EPIKRISE_MAIN_ADDITIONAL_MAX) issues.push('main_additional_max');
  if (epikriseHasMarkering(form.kind) && form.markering !== 'none') {
    if (!form.recommendation.trim()) issues.push('recommendation_missing');
    const parsed = parseMarkup(trimEnd(form.recommendation));
    if (parsed.problem) issues.push('recommendation_markup');
    else if (parsed.plain.length > EPIKRISE_RECOMMENDATION_MAX) issues.push('recommendation_too_long');
  }
  if (EPIKRISE_SECTIONS.some((s) => parseMarkup(form.sections[s.id] ?? '').problem)) issues.push('text_markup');
  else if (epikriseTextTotal(form) > EPIKRISE_TEXT01_LIMIT[form.kind]) issues.push('text_too_long');
  const contact = epikriseContact(form, signer);
  if (epikriseHasRadiologyExtras(form.kind) && signer && !contact.name) issues.push('signer_missing');
  if (contact.title.length > EPIKRISE_CONTACT_MAX || contact.name.length > EPIKRISE_CONTACT_MAX) {
    issues.push('contact_too_long');
  }
  const relativeCount = form.relatives.length + (form.includeReferralRelative && referralRelative ? 1 : 0);
  if (relativeCount > EPIKRISE_RELATIVES_MAX) issues.push('relatives_max');
  if (form.relatives.some((r) => !r.surname.trim())) issues.push('relative_surname');
  const date = /^\d{4}-\d{2}-\d{2}$/;
  if (epikriseHasRadiologyExtras(form.kind)) {
    if (form.otherDiagnoses.length > EPIKRISE_OTHER_MAX) issues.push('other_max');
    if (form.otherDiagnoses.some((d) => !d.code.trim() || !d.text.trim())) issues.push('other_incomplete');
    if (
      form.otherDiagnoses.some(
        (d) => d.code.trim().length > EPIKRISE_DIAGNOSE_CODE_MAX || d.text.trim().length > EPIKRISE_DIAGNOSE_TEXT_MAX,
      )
    ) {
      issues.push('other_too_long');
    }
    if (form.otherDiagnoses.some((d) => d.time && !d.date)) issues.push('other_time_without_date');
    if (form.otherDiagnoses.some((d) => d.date && !date.test(d.date)) && !issues.includes('dates')) issues.push('dates');
  }
  if ((form.start.date && !date.test(form.start.date)) || (form.end.date && !date.test(form.end.date))) issues.push('dates');
  else if (form.start.date && form.end.date) {
    const a = `${form.start.date}T${form.start.time || '00:00'}`;
    const b = `${form.end.date}T${form.end.time || '00:00'}`;
    if (b < a) issues.push('end_before_start');
  }
  return issues;
}

/** Wrap the selection in [tag]…[/tag]. Refused (unchanged) when it would nest or cross markup. */
export function wrapSelection(
  text: string,
  start: number,
  end: number,
  tag: 'b' | 'i' | 'u',
): { text: string; start: number; end: number; changed: boolean } {
  if (end <= start) return { text, start, end, changed: false };
  const next = `${text.slice(0, start)}[${tag}]${text.slice(start, end)}[/${tag}]${text.slice(end)}`;
  if (parseMarkup(next).problem) return { text, start, end, changed: false };
  return { text: next, start: start + 3, end: end + 3, changed: true };
}

/** SOR envelope types an epikrise receiver must accept: XDIS0x (XML) or DIS0x (EDIFACT). */
export function receivesEpikriseKind(kind: EpikriseKind, organisation: { ediReceiveTypes: readonly string[] | null | undefined }) {
  const types = organisation.ediReceiveTypes ?? [];
  return types.includes(kind) || types.includes(kind.slice(1));
}

/** Labels for start/end per type (XSD element names). */
export const EPIKRISE_DATE_ELEMENTS: Readonly<Record<EpikriseKind, { start: string; end: string }>> = {
  XDIS01: { start: 'Admission', end: 'Discharge' },
  XDIS02: { start: 'OutPatientFirstVisit', end: 'OutPatientLastVisit' },
  XDIS03: { start: 'CasualtyWardFirstVisit', end: 'CasualtyWardLastVisit' },
  XDIS05: { start: 'ExaminationStart', end: 'ExaminationEnd' },
};

/** DIS01-S 4.28: the received referral's bidiagnoser (medcom_details.additionalDiagnoses), carried into the epikrise's Referral block. */
export function referralAdditionalDiagnoses(details: Record<string, unknown> | null | undefined): Array<{ code: string | null; text: string | null }> {
  const list = details?.additionalDiagnoses;
  if (!Array.isArray(list)) return [];
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  return list
    .filter((d): d is Record<string, unknown> => typeof d === 'object' && d !== null && !Array.isArray(d))
    .map((d) => ({ code: str(d.diagnoseCode), text: str(d.diagnoseText) }));
}
